import React from "react";
import "./GoogleVerifiedAddressInput.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function loadPlacesLibrary() {
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error("Google Maps API key is not configured."));
  }
  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary("places");
  }
  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise.then((google) =>
      google.maps.importLibrary("places")
    );
  }

  // Match the shared loader used in the existing Homebuyer address input.
  const existing = document.getElementById("legup-google-maps-script");
  if (existing && !window.google?.maps?.importLibrary) {
    return Promise.reject(new Error("Google Maps is still loading. Please try again."));
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = "__legupGoogleMapsLoaded";
    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google);
    };
    const script = document.createElement("script");
    script.id = "legup-google-maps-script";
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      loading: "async",
      callback: callbackName,
      v: "weekly",
    }).toString()}`;
    script.onerror = () => {
      delete window[callbackName];
      window.__legupGoogleMapsPromise = null;
      reject(new Error("Unable to load Google Places. Check the API key and allowed domains."));
    };
    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise.then((google) =>
    google.maps.importLibrary("places")
  );
}

function addressPart(place, type, shortText = false) {
  const item = place?.addressComponents?.find((p) => p?.types?.includes(type));
  return shortText ? item?.shortText || "" : item?.longText || "";
}

/**
 * Google Places-assisted street-address selection.
 * A selected place ID is evidence of a Places match, not verification that
 * the OSS operates at that address. The backend must re-verify the place ID.
 */
export default function GoogleVerifiedAddressInput({
  value,
  countryCode,
  onChange,
  onSelect,
  placeholder = "Search for an address",
  invalid = false,
}) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [error, setError] = React.useState("");
  const timerRef = React.useRef(null);
  const requestIdRef = React.useRef(0);
  const sessionRef = React.useRef(null);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    // Invalidate pending suggestions when the country changes.
    requestIdRef.current += 1;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setSuggestions([]);
    setError("");
    setSearching(false);
  }, [countryCode]);

  React.useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      requestIdRef.current += 1;
    };
  }, []);

  function handleChange(event) {
    const nextValue = event.target.value;
    onChange(nextValue); // Parent clears stale place ID on every manual edit.
    setError("");
    if (timerRef.current) window.clearTimeout(timerRef.current);
    const requestId = ++requestIdRef.current;
    const query = nextValue.trim();

    if (!countryCode) {
      setSuggestions([]);
      setSearching(false);
      setError("Select the organisation's country first.");
      return;
    }
    if (query.length < 4) {
      setSuggestions([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    timerRef.current = window.setTimeout(async () => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await loadPlacesLibrary();
        if (!sessionRef.current) {
          sessionRef.current = new AutocompleteSessionToken();
        }
        const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: sessionRef.current,
          includedRegionCodes: [String(countryCode).toLowerCase()],
        });
        if (requestId !== requestIdRef.current) return;
        const found = (response?.suggestions || [])
          .map((suggestion) => suggestion?.placePrediction)
          .filter(Boolean);
        setSuggestions(found);
      } catch (requestError) {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setError(requestError?.message || "Could not search for addresses.");
      } finally {
        if (requestId === requestIdRef.current) setSearching(false);
      }
    }, 300);
  }

  async function selectPrediction(prediction) {
    try {
      setSearching(true);
      setError("");
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["id", "formattedAddress", "addressComponents"] });
      const placeCountry = addressPart(place, "country", true).toUpperCase();
      const street = addressPart(place, "route");
      const number = addressPart(place, "street_number");
      const town =
        addressPart(place, "locality") ||
        addressPart(place, "postal_town") ||
        addressPart(place, "administrative_area_level_3");

      if (!place?.id || !street || !number || !town) {
        setSuggestions([]);
        setError("Select a complete street address with a building number and city.");
        return;
      }
      if (placeCountry !== String(countryCode).toUpperCase()) {
        setSuggestions([]);
        setError("The selected address must be in the organisation's country.");
        return;
      }
      const address = place.formattedAddress || `${street} ${number}, ${town}`;
      requestIdRef.current += 1;
      sessionRef.current = null;
      setSuggestions([]);
      onSelect({ address, placeId: place.id, countryCode: placeCountry });
    } catch (requestError) {
      setError(requestError?.message || "Unable to select this address.");
    } finally {
      setSearching(false);
    }
  }

  return (
    <div ref={rootRef} className="legup-place-input">
      <input
        name="company_address"
        type="text"
        autoComplete="street-address"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={!countryCode}
        aria-invalid={invalid || Boolean(error)}
        aria-autocomplete="list"
        aria-expanded={suggestions.length > 0}
        className={invalid || error ? "oss-company-input-error" : ""}
      />
      {searching && <small className="legup-place-status">Searching addresses…</small>}
      {error && <small className="legup-place-error" role="alert">{error}</small>}
      {suggestions.length > 0 && (
        <div className="legup-place-options" role="listbox" aria-label="Address suggestions">
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              key={`${suggestion.placeId || suggestion.text?.text || "address"}-${index}`}
              role="option"
              aria-selected={false}
              onClick={() => selectPrediction(suggestion)}
            >
              {suggestion.text?.text || "Select address"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
